import * as React from 'react'
import { useIntl } from 'react-intl'
import productLabels from 'i18n/productLabels'
import styled from 'styled-components'
import { rem } from 'polished'
import config from 'constants/config'
import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import TextLink from 'LuxKit/TextLink'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import AtolProtectedTooltip from 'components/OfferPage/AtolProtectedTooltip'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import type { DisplayTotals } from 'lib/hotels/bedbankUtils'
import Group from 'components/utils/Group'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'

import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowValueDiscount from 'LuxKit/PricePoints/Value/PriceRowValueDiscount'
import PriceRowFlightPackage from 'LuxKit/PricePoints/PriceRowFlightPackage'
import PriceRowCaption from 'LuxKit/PricePoints/PriceRowCaption'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import AccommodationPackageTilePricingBlocks from 'components/AccommodationKit/AccommodationPackageTilePricingBlocks'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import PayLaterPill from 'components/Common/PayLaterPill'
import PayAtPropertyPill from 'components/Common/PayAtPropertyPill'
import AgentCommissionPanel from 'agentHub/components/AgentCommissionPanel'
import useHotelFlightsCtaImpression from 'components/OfferPage/hooks/useHotelFlightsCtaImpression'
const PriceDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: ${rem(4)};
`

const FlightPrice = styled(PriceDetails)`
  margin-top: ${rem(8)};
`

const ChangeDepartureWrapper = styled.div`
  margin-bottom: auto;
  text-align: center;
`

export interface RoomWithSummaryData {
  room: OfferPageRoom
  hotelOnly: {
    package: App.BedbankPackage
    rate?: App.BedbankRate
    sell?: App.BedbankSellPriceInfo
    promotion?: App.BedbankPromotion
    capacityValid: boolean
  }
  bundle: {
    package: App.BedbankPackage
    rate?: App.BedbankRate
    sell?: App.BedbankSellPriceInfo
    promotion?: App.BedbankPromotion
    capacityValid: boolean
  }
}

interface Props {
  rooms: Array<App.Occupants>
  offer: App.BedbankOffer
  flightOrigin?: App.Airport
  flightPrice?: number
  duration: number
  departureTooEarly?: boolean
  flightsEnabled: boolean
  onViewPackageOptions?: () => void
  onToggleFilter?: (e: React.MouseEvent<HTMLElement>) => void
  alignment?: 'center' | 'start'
  luxPlusLPPEnabled: boolean
  showPayAtPropertyPill: boolean
  showHotelPriceWithFlights: boolean
  canPayLater: boolean
  displayDiscount: number
  nightsToDisplay?: number
  totalPrices: DisplayTotals
  postTaxDiscount?: number
  saleUnit: string
  taxesAndFees: number
  propertyFees: number
  hotelPrice: number
  hotelMemberPrice: number
  hotelValue?: number
  bundlePropertyFees: number
  bundlePrice: number
  bundleMemberPrice: number
  bundleTaxesAndFees: number
  pointsEarnCalculationRequests: Array<App.LoyaltyPointsEarnCalculationRequest | undefined>
  hasFlights: boolean
  departureTooFarInFuture: boolean
  promoteAsBundle: boolean
}

function BedbankSummaryPriceDetailsView(props: Props) {
  const intl = useIntl()
  const {
    rooms,
    offer,
    flightOrigin,
    flightPrice,
    departureTooEarly,
    flightsEnabled,
    duration,
    onViewPackageOptions,
    onToggleFilter,
    alignment = 'center',
    luxPlusLPPEnabled,
    showPayAtPropertyPill,
    showHotelPriceWithFlights,
    canPayLater,
    displayDiscount,
    nightsToDisplay,
    totalPrices,
    postTaxDiscount,
    saleUnit,
    taxesAndFees,
    propertyFees,
    hotelPrice,
    hotelMemberPrice,
    hotelValue,
    bundlePropertyFees,
    bundlePrice,
    bundleMemberPrice,
    bundleTaxesAndFees,
    pointsEarnCalculationRequests,
    hasFlights,
    departureTooFarInFuture,
    promoteAsBundle,
  } = props

  const viewTravelSynonymOptionsLabel = intl.formatMessage(
    {
      id: 'ahrceC82sHtFQSw6Fq88',
      defaultMessage: 'View {travelSynonym} options',
      description: 'Button text to view travel options for bedbank summary price details',
    },
    { travelSynonym: config.brandConfig.travelSynonym },
  )

  const viewOptionsLabel = intl.formatMessage({
    id: 'dofh5UAf_iynHAzCUTtH',
    defaultMessage: 'View options',
    description: 'Button text to view package options for bedbank summary price details',
  })

  const noFlightsFromAirportMessage = intl.formatMessage(
    {
      id: 'b544xJUAyEA_feIc6aTT',
      defaultMessage: 'No flights available from {airportName}. Please select another airport.',
      description: 'Error message when no flights are available from the selected airport on bedbank offer page',
    },
    { airportName: flightOrigin?.name },
  )

  const changeDepartureAirportLabel = intl.formatMessage({
    id: 'DrbORzPwdTQ0JQ9hCLxp',
    defaultMessage: 'Change departure airport',
    description: 'Link text to change departure airport on bedbank offer page',
  })

  const addFlightsInfoMessage = intl.formatMessage({
    id: 'BY83Y6V0SckyA0YHQY6w',
    defaultMessage: 'Add flights to the hotel package. Select from all major airlines at the next step.',
    description: 'Info message about adding flights to hotel package on bedbank offer page',
  })

  const flightsAdvanceBookingMessage = intl.formatMessage({
    id: 'HEcH1w6rEidNRQp4OOiy',
    defaultMessage: 'Flights can only be booked up to 11 months in advance.',
    description: 'Error message when flights are too far in advance on bedbank offer page',
  })

  const nightsLabel = intl.formatMessage(
    {
      id: 'lV8s0wcEHYgwFYrQ8oqw',
      defaultMessage: '{nights, plural, one {# night} other {# nights}}',
      description: 'Night count label for bedbank summary price caption',
    },
    { nights: nightsToDisplay },
  )
  const roomsLabel = intl.formatMessage(
    {
      id: 'PHEcJOOYE0FT86WJ0KoD',
      defaultMessage: '{rooms, plural, one {# room} other {# rooms}}',
      description: 'Room count label for bedbank summary price caption',
    },
    { rooms: rooms.length },
  )
  const nightsPriceCaptionText = rooms.length > 1
    ? intl.formatMessage(
        {
          id: 'rZocnNhWzuREed7qFckB',
          defaultMessage: '{nightsLabel}, {roomsLabel} from',
          description: 'Bedbank summary price caption with night count, room count, and from label',
        },
        { nightsLabel, roomsLabel },
      )
    : intl.formatMessage(
        {
          id: '3PL084euS401j2P6bj1A',
          defaultMessage: '{nightsLabel} from',
          description: 'Bedbank summary price caption with night count and from label',
        },
        { nightsLabel },
      )

  const nightsFlightsPriceCaptionText = rooms.length > 1
    ? intl.formatMessage(
        {
          id: 'ZADDm1qJYFskFHHB_e9V',
          defaultMessage: '{nightsLabel}, {roomsLabel} + flights from',
          description: 'Bedbank summary price caption with night count, room count, flights, and from label',
        },
        { nightsLabel, roomsLabel },
      )
    : intl.formatMessage(
        {
          id: 'zZs2NTweuNhM833ah7zy',
          defaultMessage: '{nightsLabel} + flights from',
          description: 'Bedbank summary price caption with night count, flights, and from label',
        },
        { nightsLabel },
      )

  const flightsWithin24HoursMessage = intl.formatMessage({
    id: 'd3Uv-UaGi7Hwq9wpt2z7',
    defaultMessage: 'Flights cannot be booked online when check-in is within 24 hours',
    description: 'Error message when check-in is within 24 hours on bedbank offer page',
  })
  const shouldTrackHotelFlightsCtaImpression = showHotelPriceWithFlights || (flightsEnabled && !promoteAsBundle)
  const hasDisplayPriceAndCta = showHotelPriceWithFlights || (hasFlights && !departureTooEarly)
  const hotelFlightsCtaImpressionRef = useHotelFlightsCtaImpression(hasDisplayPriceAndCta, shouldTrackHotelFlightsCtaImpression)

  if (showHotelPriceWithFlights) {
    return <Group direction="vertical" fullWidth gap={16} horizontalAlign="center">
      <Group direction="horizontal" gap={4} fullWidth horizontalAlign="start">
        {canPayLater && <PayLaterPill />}
        {displayDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && <PriceRowValueDiscount
          type="estimate"
          discountPercentage={displayDiscount}
        />}
      </Group>
      <AccommodationPackageTilePricingBlocks>
        <Group direction="vertical" gap={12} verticalAlign="space-between">
          <Group gap={8} direction="vertical" verticalAlign="space-evenly">
            <Group direction="vertical" gap={12}>
              <BodyText variant="large" weight="bold">{intl.formatMessage(productLabels.hotel)}
              </BodyText>
              <Group direction="vertical">
                <Group direction="vertical" gap={4}>
                  <Group direction="vertical">
                    {!!nightsToDisplay && <PriceRowPriceCaption>
                      {nightsPriceCaptionText}
                    </PriceRowPriceCaption>}
                    <LuxPlusPriceGuard
                      showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                      fallbackPrice={<PriceRowPrice
                        data-testid="bedbank-hotel-flights-summary-price"
                        size="L"
                        price={totalPrices.totals.price}
                        saleUnit={saleUnit}
                      />}
                    >
                      <LuxPlusPriceStack
                        size="S"
                        price={totalPrices.totals.price}
                        memberPrice={totalPrices.totals.memberPrice}
                        saleUnit={saleUnit}
                      />
                    </LuxPlusPriceGuard>
                    {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
                  </Group>
                </Group>
                {taxesAndFees > 0 && <PriceRowTaxesAndFees
                  endIcon={<OfferPriceDetailsRow
                    offerPage
                    offer={offer}
                    rooms={rooms}
                    propertyFees={propertyFees}
                    hotelPrice={hotelPrice}
                    hotelMemberPrice={hotelMemberPrice}
                    taxesAndFees={taxesAndFees}
                    flightPrice={flightPrice}
                    showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
                    duration={duration ?? 0}
                    postTaxDiscount={postTaxDiscount}
                    trigger="info-icon"
                    cta={<TextButton kind="primary" fit="flex">
                      {viewTravelSynonymOptionsLabel}
                    </TextButton>}
                    onCtaAfterClose={onViewPackageOptions}
                  />}
                />}
                <AgentCommissionPanel
                  offerId={offer.id}
                  offerTotal={totalPrices.totals.price ?? 0}
                  retailValue={totalPrices.totals.value}
                  memberPrice={luxPlusLPPEnabled ? totalPrices.totals.memberPrice : undefined}
                />
                {!!displayDiscount && !!hotelValue && <PriceRowValueDiscountWithCaption
                  size="L"
                  originalValue={totalPrices.totals.value}
                />}
              </Group>
            </Group>
          </Group>
          <TextButton
            kind="secondary"
            data-testid="OfferAvailable"
            onClick={onViewPackageOptions}
            fit="flex"
          >
            {viewOptionsLabel}
          </TextButton>
        </Group>
        <div ref={hotelFlightsCtaImpressionRef}>
          <Group direction="vertical" gap={12} verticalAlign="space-between">
            <Group gap={8} direction="vertical" verticalAlign="space-evenly">
              <Group direction="vertical" gap={12} verticalAlign="space-evenly">
                <BodyTextBlock startIcon={<LinePlaneDepartureIcon />} variant="large" weight="bold">
                  {intl.formatMessage({
                    id: 'ajF1iNmTeKc1ylVly1ny',
                    defaultMessage: 'Hotel + flights',
                    description: 'Hotel and flights label for bedbank summary price details',
                  })}
                </BodyTextBlock>
                <Group direction="vertical">
                  <Group direction="vertical" gap={4}>
                    <Group direction="vertical">
                      {!!nightsToDisplay && <PriceRowPriceCaption>
                        {nightsFlightsPriceCaptionText}
                      </PriceRowPriceCaption>}
                      <LuxPlusPriceGuard
                        showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                        fallbackPrice={<PriceRowPrice
                          data-testid="bedbank-hotel-flights-summary-price"
                          size="L"
                          price={totalPrices.totals.flightBundlePrice}
                          saleUnit={saleUnit}
                        />}
                      >
                        <LuxPlusPriceStack
                          size="S"
                          price={totalPrices.totals.flightBundlePrice}
                          memberPrice={totalPrices.totals.flightBundleMemberPrice}
                          saleUnit={saleUnit}
                        />
                      </LuxPlusPriceGuard>
                      {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
                    </Group>
                  </Group>
                  {taxesAndFees > 0 && <PriceRowTaxesAndFees
                    endIcon={<OfferPriceDetailsRow
                      offerPage
                      offer={offer}
                      rooms={rooms}
                      propertyFees={bundlePropertyFees}
                      hotelPrice={bundlePrice}
                      hotelMemberPrice={bundleMemberPrice}
                      taxesAndFees={bundleTaxesAndFees}
                      flightPrice={flightPrice}
                      showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
                      duration={duration ?? 0}
                      trigger="info-icon"
                      cta={<TextButton kind="primary" fit="flex">
                        {viewTravelSynonymOptionsLabel}
                      </TextButton>}
                      onCtaAfterClose={onViewPackageOptions}
                    />}
                  />}
                  <AgentCommissionPanel
                    offerId={offer.id}
                    offerTotal={totalPrices.totals.price ?? 0}
                    retailValue={totalPrices.totals.value}
                    memberPrice={luxPlusLPPEnabled ? totalPrices.totals.flightBundleMemberPrice : undefined}
                  />
                  {!!displayDiscount && !!hotelValue && <PriceRowValueDiscountWithCaption
                    size="L"
                    originalValue={totalPrices.totals.flightBundleValue}
                  />}
                </Group>
              </Group>
            </Group>
            <TextButton
              kind="secondary"
              data-testid="OfferAvailable"
              onClick={onViewPackageOptions}
              fit="flex"
            >
              {viewOptionsLabel}
            </TextButton>
          </Group>
        </div>
      </AccommodationPackageTilePricingBlocks>
      <LoyaltyPointsEarn horizontalAlign="center" calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" offer={offer} />
    </Group>
  }

  return (
    <Group direction="vertical" horizontalAlign={alignment}>
      <Group direction="vertical" horizontalAlign={alignment} gap={2}>
        <Group direction="vertical" horizontalAlign={alignment} gap={12}>
          <Group direction="vertical" horizontalAlign={alignment}>
            <Group direction="vertical" horizontalAlign={alignment} gap={4}>
              <Group direction="horizontal" gap={4}>
                {canPayLater && <PayLaterPill />}
                {showPayAtPropertyPill && <PayAtPropertyPill />}
                {displayDiscount >= MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE && <PriceRowValueDiscount
                  type="estimate"
                  discountPercentage={displayDiscount}
                />}
              </Group>
              <Group direction="vertical" horizontalAlign={alignment}>
                {!!nightsToDisplay && <PriceRowPriceCaption>
                  {promoteAsBundle ? nightsFlightsPriceCaptionText : nightsPriceCaptionText}
                </PriceRowPriceCaption>}
                <LuxPlusPriceGuard
                  showMemberPrice={(totalPrices.totals.memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
                  fallbackPrice={<PriceRowPrice
                    data-testid="bedbank-hotel-summary-price"
                    size="L"
                    price={promoteAsBundle ? totalPrices.totals.flightBundlePrice : totalPrices.totals.price}
                    saleUnit={saleUnit}
                  />}
                >
                  <LuxPlusPriceStack
                    horizontalAlign={alignment}
                    size="L"
                    price={promoteAsBundle ? totalPrices.totals.flightBundlePrice : totalPrices.totals.price}
                    memberPrice={promoteAsBundle ? totalPrices.totals.flightBundleMemberPrice : totalPrices.totals.memberPrice}
                    saleUnit={saleUnit}
                  />
                </LuxPlusPriceGuard>
                {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
              </Group>
            </Group>
            {taxesAndFees > 0 && <PriceRowTaxesAndFees
              endIcon={<OfferPriceDetailsRow
                offerPage
                offer={offer}
                rooms={rooms}
                propertyFees={promoteAsBundle ? bundlePropertyFees : propertyFees}
                hotelPrice={promoteAsBundle ? bundlePrice : hotelPrice}
                hotelMemberPrice={promoteAsBundle ? bundleMemberPrice : hotelMemberPrice}
                taxesAndFees={promoteAsBundle ? bundleTaxesAndFees : taxesAndFees}
                flightPrice={promoteAsBundle ? flightPrice : undefined}
                showMemberPrice={luxPlusLPPEnabled && hotelMemberPrice > 0}
                duration={duration ?? 0}
                postTaxDiscount={promoteAsBundle ? undefined : postTaxDiscount}
                trigger="info-icon"
                cta={<TextButton kind="primary" fit="flex">
                  {viewTravelSynonymOptionsLabel}
                </TextButton>}
                onCtaAfterClose={onViewPackageOptions}
              />}
            />}
            {!!displayDiscount && !!hotelValue && <PriceRowValueDiscountWithCaption
              size="L"
              originalValue={promoteAsBundle ? totalPrices.totals.flightBundleValue : totalPrices.totals.value}
            />}
          </Group>
          <AgentCommissionPanel
            offerId={offer.id}
            offerTotal={totalPrices.totals.price ?? 0}
            retailValue={totalPrices.totals.value}
            memberPrice={luxPlusLPPEnabled
              ? (promoteAsBundle ? totalPrices.totals.flightBundleMemberPrice : totalPrices.totals.memberPrice)
              : undefined}
          />
          <LoyaltyPointsEarn horizontalAlign="center" calculationRequests={pointsEarnCalculationRequests} calculationType="estimate" offer={offer} />
        </Group>
        {flightsEnabled && !promoteAsBundle &&
          <FlightPrice ref={hotelFlightsCtaImpressionRef}>
            {!hasFlights && flightOrigin && flightOrigin.code === offer.flights?.airportCode &&
              <ChangeDepartureWrapper>
                <PriceRowCaption>
                  {noFlightsFromAirportMessage}
                </PriceRowCaption>
                <TextLink
                  onClick={onToggleFilter}
                  data-filter="airport"
                >
                  {changeDepartureAirportLabel}
                </TextLink>
              </ChangeDepartureWrapper>}
            {!hasFlights && flightOrigin?.code !== offer.flights?.airportCode && !departureTooFarInFuture && !departureTooEarly &&
              <PriceRowCaption>
                {addFlightsInfoMessage}
              </PriceRowCaption>}
            {!hasFlights && departureTooFarInFuture &&
              <PriceRowCaption>
                {flightsAdvanceBookingMessage}
              </PriceRowCaption>}
            {flightOrigin?.code !== offer.flights?.airportCode && departureTooEarly &&
              <ChangeDepartureWrapper>
                <BodyText variant="small" colour="light-foreground-alt">
                  {flightsWithin24HoursMessage}
                </BodyText>
                <TextLink
                  onClick={onToggleFilter}
                  data-filter="checkin"
                >
                  {changeDepartureAirportLabel}
                </TextLink>
              </ChangeDepartureWrapper>}
            {hasFlights && !departureTooEarly &&
              <Group direction="vertical" horizontalAlign="center">
                <PriceRowFlightPackage
                  product="hotel"
                  price={totalPrices.totals.flightBundlePrice}
                  pricePerPerson={offer.displayPricingAsPerPerson}
                />
              </Group>}
            <AtolProtectedTooltip />
          </FlightPrice>}
      </Group>
    </Group>
  )
}

export default BedbankSummaryPriceDetailsView
